import StyledInput from 'components/styledInput/StyledInput';
import { sectorsInfoList } from 'helper/helper';
import { useEffect, useState } from 'react';
import ApiDataBase from 'server';
import LocationSection from '../slideOverAddMissionGeneral/locationSection';
import ResponsibleSection from '../slideOverAddMissionGeneral/responsibleSection';
import TemplateToolsSection from './templateToolsSection';
import TemplateRiskSection from './templateRiskSection';
import TemplateTasksSection from './templateTasksSection';
import SummaryField from './summaryField';
import TemplateNameField from './TemplateNameField';
import { SUMMARY_ROWS, buildTemplateSummary } from './templateSummary';
import { useRiskyPositions } from './missionTemplateRiskyPositions';
import {
  DRESS_CODE_MAX_LENGTH,
  TEMPLATE_NAME_TAKEN_MESSAGE,
  activityFromQualificationType,
  getQualificationTaskProposals,
  getTemplateNameSuffix,
  isTemplateNameTaken,
  templateNamePrefix,
  withQualificationTasks,
} from './templateFormData';
import { templateText } from './templateTexts';

const SECTORS_INFO = sectorsInfoList();

const Section = ({ title, description, children }) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col">
      <span className="ft-xl font-medium text-gray-900">{title}</span>
      {description ? (
        <span className="ft-sm text-gray-500">{description}</span>
      ) : null}
    </div>
    {children}
  </div>
);

const Divider = () => <div className="border-t border-gray-100" />;

const SectorGrid = ({ sectors, selectedSector, onSelect }) => (
  <div className="grid grid-cols-3 gap-3">
    {sectors.map((key) => (
      <button
        key={key}
        type="button"
        onClick={() => onSelect(key)}
        className={`flex flex-col items-center justify-center gap-1 h-24 border rounded-lg ${
          selectedSector === key
            ? 'bg-yellow-50 border-yellow-500'
            : 'border-gray-200 hover:bg-gray-100'
        }`}
      >
        <span className="text-2xl" aria-hidden="true">
          {SECTORS_INFO[key]?.icon || '❓'}
        </span>
        <span className="ft-sm text-gray-900">
          {SECTORS_INFO[key]?.name || key}
        </span>
      </button>
    ))}
  </div>
);

/** Creating or editing a job description for a company, step by step, with the
 *  suggestions the admin mission creation already offers (qualifications and
 *  managers seen on that company's recent missions). */
const TemplateForm = ({
  stepKey,
  form,
  setForm,
  idCompany,
  idLocation,
  onEditStep,
  siblingTemplates,
  editedTemplateId,
}) => {
  const token = localStorage.getItem('xsrfToken');
  const riskyPositions = useRiskyPositions();
  const [sectors, setSectors] = useState([]);
  const [activeQualifications, setActiveQualifications] = useState([]);
  const [toolOptions, setToolOptions] = useState([]);
  const [licenceOptions, setLicenceOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [smic, setSmic] = useState('');
  const [qualificationSuggestions, setQualificationSuggestions] = useState([]);
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (!token) return;
    ApiDataBase.getTypesQualifications({ token }, setSectors, (err) => {
      if (err.name !== 'CanceledError') console.error(err);
    });
    ApiDataBase.getQualificationsActive(
      { token },
      setActiveQualifications,
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
    ApiDataBase.getListTools(
      (data) => setToolOptions(data.map((tool) => tool.name)),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
    ApiDataBase.getAllLicence(
      (data) => setLicenceOptions(data.map((licence) => licence.name)),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
    ApiDataBase.getListLanguages(
      (data) => setLanguageOptions(data.map((language) => language.name)),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
    ApiDataBase.getMinimumWage(
      (data) => {
        if (data?.hourly?.gross) setSmic(String(data.hourly.gross));
      },
      (err) => console.error(err)
    );
  }, [token]);

  useEffect(() => {
    if (!token || !idCompany) return;
    ApiDataBase.getCompanyQualificationSuggestionsFromRecentMissions(
      { token, idCompany },
      setQualificationSuggestions,
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
    ApiDataBase.getRecentContactNamesForMissionCreation(
      { token, idCompany },
      setContactSuggestions,
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
    ApiDataBase.getListEmployeesForMissionCreation(
      { token, idCompany },
      setEmployees,
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  }, [token, idCompany]);

  // An edited template only carries the qualification name: its id, and the
  // sector it belongs to, come from the referential.
  useEffect(() => {
    if (activeQualifications.length === 0) return;
    setForm((prev) => {
      if (prev.qualification || !prev.qualificationLabel) return prev;
      const match = activeQualifications.find(
        (item) => item.name === prev.qualificationLabel
      );
      if (!match) return prev;
      // Resolving the edited qualification is no change of qualification: the
      // stored selection stays, only the proposals are filled in.
      return {
        ...prev,
        qualification: match._id,
        sector: match.type,
        taskProposals: getQualificationTaskProposals(match),
      };
    });
  }, [activeQualifications, setForm]);

  const qualificationList = activeQualifications
    .filter((item) => !form.sector || item.type === form.sector)
    .map((item) => ({
      value: item._id,
      label: item.name,
      type: item.type,
      checked: item._id === form.qualification,
    }));

  const selectQualification = (id, label, type) =>
    setForm((prev) => {
      const previousPrefix = templateNamePrefix(prev.qualificationLabel);
      const suffix = prev.name.startsWith(previousPrefix)
        ? prev.name.slice(previousPrefix.length)
        : prev.name;
      const withTasks =
        id === prev.qualification
          ? prev
          : withQualificationTasks(
              prev,
              activeQualifications.find((item) => item._id === id)
            );
      return {
        ...withTasks,
        qualification: id,
        qualificationLabel: label,
        sector: type || prev.sector,
        activity: activityFromQualificationType(type || prev.sector),
        // The name always opens with the qualification; only the rest is typed.
        name: templateNamePrefix(label) + suffix,
      };
    });

  const onQualificationChange = (updated) => {
    const checked = updated.find((item) => item.checked);
    selectQualification(
      checked?.value || '',
      checked?.label || '',
      checked?.type
    );
  };

  const onSectorToggle = (sector) =>
    setForm((prev) =>
      prev.sector === sector
        ? { ...prev, sector: '' }
        : { ...prev, sector, activity: activityFromQualificationType(sector) }
    );

  const namePrefix = templateNamePrefix(form.qualificationLabel);
  const isNameTaken = isTemplateNameTaken(
    form.name,
    siblingTemplates,
    editedTemplateId
  );
  const nameSuffix = getTemplateNameSuffix(form.name, form.qualificationLabel);
  const onNameSuffixChange = (suffix) =>
    setForm((prev) => ({ ...prev, name: namePrefix + suffix }));

  const onSalaryChange = (value) =>
    setForm((prev) => ({
      ...prev,
      salary: value.replace(/,/g, '.').replace(/[^0-9.]/g, ''),
    }));

  const toggleIndexedToMinimumWage = () =>
    setForm((prev) => {
      const isIndexed = !prev.isIndexedToMinimumWage;
      return {
        ...prev,
        isIndexedToMinimumWage: isIndexed,
        salary: isIndexed && smic ? smic : prev.salary,
      };
    });

  const toCheckboxList = (options, selection) =>
    options.map((name) => ({
      value: name,
      label: name,
      checked: selection.includes(name),
    }));

  const onListChange = (field) => (updated) =>
    setForm((prev) => ({
      ...prev,
      [field]: updated.filter((item) => item.checked).map((item) => item.value),
    }));

  if (stepKey === 'activity') {
    return (
      <div className="flex flex-col gap-6">
        <Section
          title="Secteur d'activité"
          description="Sélectionnez le domaine professionnel concerné par la mission."
        >
          <SectorGrid
            sectors={sectors}
            selectedSector={form.sector}
            onSelect={onSectorToggle}
          />
        </Section>
        <Divider />
        <Section
          title="Qualification"
          description="Indiquez la qualification du poste à pourvoir pour cette mission."
        >
          <StyledInput
            type="selectMultipleRadio"
            value={form.qualificationLabel || 'Sélectionner'}
            list={qualificationList}
            onChange={onQualificationChange}
            isSearchable
            dropdownFullWidth
            listSuggestions={qualificationSuggestions}
            onClickSuggestion={(suggestion) =>
              selectQualification(suggestion.id, suggestion.name, suggestion.type)
            }
          />
        </Section>
        <Divider />
        <Section
          title="Nom du modèle"
          description="L'intitulé de la mission (qualification suivie de « - ») est fixé automatiquement. Vous pouvez uniquement compléter après le tiret (lieu, précision, etc.)."
        >
          <TemplateNameField
            label="Nom"
            prefix={namePrefix || '—'}
            value={nameSuffix}
            onChange={(event) => onNameSuffixChange(event.target.value)}
            placeholder={
              form.qualificationLabel
                ? 'Complément'
                : "Choisissez d'abord une qualification"
            }
            isDisabled={!form.qualificationLabel}
            error={isNameTaken ? `${TEMPLATE_NAME_TAKEN_MESSAGE}.` : null}
          />
        </Section>
      </div>
    );
  }

  if (stepKey === 'risk') {
    return (
      <TemplateRiskSection
        positionAtRisk={form.position_at_risk}
        penibilite={form.penibilite}
        onChange={(conditions) =>
          setForm((prev) => ({ ...prev, ...conditions }))
        }
      />
    );
  }

  if (stepKey === 'prerequis') {
    return (
      <Section
        title="Pré-requis"
        description="Mentionnez les qualifications et matériels essentiels pour ces missions."
      >
        <TemplateToolsSection
          tools={form.tools}
          toolOptions={toolOptions}
          onChange={(tools) => setForm((prev) => ({ ...prev, tools }))}
        />
        <StyledInput
          type="selectMultipleCheckbox"
          isSearchable
          label="Permis nécessaire"
          value={
            form.licences.length > 0 ? form.licences.join(', ') : 'Sélectionner'
          }
          list={toCheckboxList(licenceOptions, form.licences)}
          onChange={onListChange('licences')}
          dropdownFullWidth
        />
        <StyledInput
          type="selectMultipleCheckbox"
          isSearchable
          label="Langue nécessaire"
          value={
            form.languages.length > 0
              ? form.languages.join(', ')
              : 'Sélectionner'
          }
          list={toCheckboxList(languageOptions, form.languages)}
          onChange={onListChange('languages')}
          dropdownFullWidth
        />
        <StyledInput
          type="textarea"
          label={templateText('dressCode.label')}
          value={form.dressCode}
          maxLength={DRESS_CODE_MAX_LENGTH}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, dressCode: event.target.value }))
          }
          placeholder={templateText('dressCode.placeholder')}
        />
      </Section>
    );
  }

  if (stepKey === 'tasks') {
    return <TemplateTasksSection form={form} setForm={setForm} />;
  }

  if (stepKey === 'locationSalary') {
    return (
      <div className="flex flex-col gap-6">
        <Section
          title="Adresse"
          description="Précisez l'adresse exacte où se déroulera la mission."
        >
          <LocationSection
            address={form.location}
            setAddress={(location) => setForm((prev) => ({ ...prev, location }))}
            dataCreateMission={{ idLocation }}
            setDataCreateMission={setForm}
            hideTitle
          />
        </Section>
        <Divider />
        <Section
          title="Salaire horaire"
          description="Indiquez le taux horaire brut associé à cette fiche de poste."
        >
          <StyledInput
            type="text"
            label="Taux horaire brut"
            name="salary"
            value={form.salary}
            onChange={(event) => onSalaryChange(event.target.value)}
            inputMode="decimal"
            isDisabled={form.isIndexedToMinimumWage}
            placeholder="Ex : 12,50"
            unit={<span className="ft-sm text-gray-500">€/heure</span>}
          />
          <div className="w-fit">
            <StyledInput
              type="checkbox"
              label={`Indexer au SMIC ${smic ? `(${smic}€/h)` : ''}`}
              checked={!!form.isIndexedToMinimumWage}
              onChange={toggleIndexedToMinimumWage}
            />
          </div>
        </Section>
        <Divider />
        <Section
          title={templateText('responsible.title')}
          description={templateText('responsible.description')}
        >
          <ResponsibleSection
            personInCharge={form.contact_name}
            setPersonInCharge={(value) =>
              setForm((prev) => ({ ...prev, contact_name: value }))
            }
            personInChargeFunction={form.contact_poste}
            setPersonInChargeFunction={(value) =>
              setForm((prev) => ({ ...prev, contact_poste: value }))
            }
            setDataCreateMission={setForm}
            lastContactSuggestions={contactSuggestions}
            listEmployees={employees}
            hideTitle
          />
        </Section>
      </div>
    );
  }

  const summary = buildTemplateSummary(form, riskyPositions);
  return (
    <Section
      title={templateText('confirmation.title')}
      description={templateText('confirmation.description')}
    >
      <div className="flex flex-col">
        {SUMMARY_ROWS.map((row) => (
          <SummaryField
            key={row.key}
            label={row.label}
            value={summary[row.key]}
            editLabel={templateText('confirmation.edit', { label: row.label })}
            onEdit={() => onEditStep(row.stepKey)}
          />
        ))}
      </div>
    </Section>
  );
};

export default TemplateForm;
